import { Card, Typography, Button, Space, Tooltip, Switch } from "antd";
import { Bar, Pie } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
  LogarithmicScale,
  Title as ChartTitle
} from "chart.js";
import {
  ExpandOutlined,
  BarChartOutlined,
  PieChartOutlined,
  PictureOutlined,
  FileExcelOutlined
} from "@ant-design/icons";
import { useState, useRef } from "react";
import dayjs from "dayjs";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  BarElement,
  ArcElement,
  ChartTooltip,
  Legend,
  ChartDataLabels,
  ChartTitle
);

const { Title } = Typography;

export function UbicacionChart({ data = [] }) {
  const [chartType, setChartType] = useState('bar'); // 'bar' o 'pie'
  const [isExpanded, setIsExpanded] = useState(false);
  const [viewOption, setViewOption] = useState('count'); // 'count' o 'percentage'
  const chartRef = useRef(null);

  // Configuración de estados y colores
  const estadosConfig = [
    { key: 'verificado', label: 'Verificado', color: '#20AC00', textColor: '#fff' },
    { key: 'enProceso', label: 'En Proceso', color: '#2979FF', textColor: '#fff' },
    { key: 'noContinua', label: 'No Continua', color: '#D62828', textColor: '#fff' },
    { key: 'noVerificado', label: 'No Verificado', color: '#B4B4B4', textColor: '#000' }
  ];
  // Verificar datos
  if (!data || data.length === 0) {
    return (
      <Card hoverable style={{ borderRadius: 12 }}>
        <Title level={5}>Participantes por Departamento</Title>
        <p>No hay datos disponibles.</p>
      </Card>
    );
  }

  // 1. LÓGICA PARA PROCESAR LOS DATOS
  // Obtener departamentos únicos
  const departamentos = [...new Set(data.map(item => item.departamento))];

  // Inicializar estructura para almacenar datos
  const datosPorDepartamento = {};
  departamentos.forEach(depto => {
    datosPorDepartamento[depto] = {};
    estadosConfig.forEach(estado => {
      datosPorDepartamento[depto][estado.key] = 0;
    });
  });

  // Llenar con datos reales
  data.forEach(item => {
    const depto = item.departamento;
    if (datosPorDepartamento[depto]) {
      estadosConfig.forEach(estado => {
        const valor = parseInt(item[estado.key] || 0);
        if (!isNaN(valor)) {
          datosPorDepartamento[depto][estado.key] += valor;
        }
      });
    }
  });

  // Calcular totales por departamento y generales
  const totalesPorDepartamento = {};
  departamentos.forEach(depto => {
    totalesPorDepartamento[depto] = estadosConfig.reduce(
      (sum, estado) => sum + datosPorDepartamento[depto][estado.key],
      0
    );
  });

  const totalGeneral = Object.values(totalesPorDepartamento).reduce((a, b) => a + b, 0);

  // Función para obtener datos según la vista seleccionada
  const getDataValues = () => {
    if (viewOption === 'count') {
      // Retorna datos en números absolutos
      return estadosConfig.map(estado => ({
        label: estado.label,
        data: departamentos.map(depto => datosPorDepartamento[depto][estado.key])
      }));
    } else {
      // Retorna datos en porcentajes
      return estadosConfig.map(estado => ({
        label: estado.label,
        data: departamentos.map(depto => {
          const totalDepto = totalesPorDepartamento[depto];
          return totalDepto > 0
            ? ((datosPorDepartamento[depto][estado.key] / totalDepto) * 100).toFixed(1)
            : 0;
        })
      }));
    }
  };

  const datasets = getDataValues();

  // 2. PREPARACIÓN DE DATOS PARA CHART.JS
  const barChartData = {
    labels: departamentos,
    datasets: datasets.map((dataset, index) => ({
      label: dataset.label,
      data: dataset.data,
      backgroundColor: estadosConfig[index].color,
      borderColor: estadosConfig[index].color,
      borderWidth: 1,
      borderRadius: 6,
      datalabels: {
        color: estadosConfig[index].textColor,
        font: {
          weight: "bold",
          size: 11
        },
      }
    }))
  };

  // Para gráfico de pastel, necesitamos aplanar los datos
  const pieChartData = {
    labels: departamentos.flatMap(depto =>
      estadosConfig.map(estado => `${depto} - ${estado.label}`)
    ),
    datasets: [{
      data: departamentos.flatMap(depto =>
        estadosConfig.map(estado => datosPorDepartamento[depto][estado.key])
      ),
      backgroundColor: departamentos.flatMap((_, deptoIndex) =>
        estadosConfig.map((estado, estadoIndex) => {
          // Variar ligeramente el color para cada departamento
          const baseColor = estadosConfig[estadoIndex].color;
          if (deptoIndex === 0) return baseColor;
          // Oscurecer ligeramente para departamentos subsiguientes
          return baseColor.replace(')', `, ${0.9 - deptoIndex * 0.1})`).replace('rgb', 'rgba');
        })
      )
    }]
  };

  // 3. OPCIONES DE CONFIGURACIÓN (reutilizables)
  const getChartOptions = (isPie = false) => {

    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 20,
            boxWidth: 12
          }
        },
        tooltip: {
          callbacks: {
            label: function (context) {

              if (isPie) {
                const fullLabel = context.label;
                const value = context.raw;
                const [depto, tipo] = fullLabel.split(' - ');
                const totalDepto = totalesPorDepartamento[depto] || 0;

                if (viewOption === 'count') {
                  const percentage = totalDepto > 0
                    ? ((value / totalDepto) * 100).toFixed(1)
                    : 0;

                  return `${tipo}: ${Number(value).toLocaleString()} (${percentage}%)`;
                } else {
                  return `${tipo}: ${value}%`;
                }
              }

              // 🔵 BARRAS
              const label = context.dataset.label || '';
              const value = context.parsed.y;
              const depto = context.label;

              if (viewOption === 'count') {
                const totalDepto = totalesPorDepartamento[depto];
                const percentage = totalDepto > 0
                  ? ((value / totalDepto) * 100).toFixed(1)
                  : 0;

                return `${label}: ${Number(value).toLocaleString()} (${percentage}%)`;
              } else {
                return `${label}: ${value}%`;
              }
            }
          }
        },
        datalabels: {
          display: !isPie,
          anchor: 'center',
          align: 'center',
          color: function (context) {
            return estadosConfig[context.datasetIndex].textColor;
          },
          font: {
            weight: 'bold',
            size: 11
          },
          formatter: (value) => {
            if (value === 0) return '';
            return viewOption === 'count'
              ? Number(value).toLocaleString()
              : `${value}%`;
          }
        }
      }
    };

    // 🔵 CONFIGURACIÓN PARA PIE
    if (isPie) {
      return {
        ...baseOptions,
        plugins: {
          ...baseOptions.plugins,
          legend: {
            position: 'right',
            labels: {
              boxWidth: 12,
              padding: 10,
              font: { size: 11 }
            }
          }
        }
      };
    }


    return {
      ...baseOptions,
      scales: {
        x: {
          stacked: true,
          grid: { color: 'rgba(0,0,0,0.05)', drawBorder: false },
          ticks: { font: { size: 11 } }
        },
        y: {
          stacked: true,
          type: 'linear',
          beginAtZero: true,

          grid: { color: 'rgba(0,0,0,0.05)', drawBorder: false },
          ticks: {
            callback: function (value) {
              return Number(value).toLocaleString();
            }
          }
        }

      }
    };
  };


  // 4. FUNCIONES DE EXPORTACIÓN (reutilizadas de tu gráfica original)
  const exportHighQualityImage = () => {
    if (!chartRef.current) {
      console.error('No se pudo encontrar el gráfico');
      return;
    }

    const canvas = chartRef.current.canvas;
    const originalWidth = canvas.width;
    const originalHeight = canvas.height;

    const exportCanvas = document.createElement('canvas');
    const ctx = exportCanvas.getContext('2d');
    exportCanvas.width = originalWidth * 2;
    exportCanvas.height = originalHeight * 2;

    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
    ctx.scale(2, 2);
    ctx.drawImage(canvas, 0, 0);

    const image = exportCanvas.toDataURL('image/png', 1.0);
    const link = document.createElement('a');
    link.href = image;
    link.download = `participantes_departamento_${dayjs().format('YYYY-MM-DD')}.png`;
    link.click();
  };

  const handleExportCSV = () => {
    const headers = ['Departamento', ...estadosConfig.map(e => e.label), 'Total'];
    const rows = departamentos.map(depto => {
      const valores = estadosConfig.map(estado => datosPorDepartamento[depto][estado.key]);
      const total = valores.reduce((a, b) => a + b, 0);
      return [depto, ...valores, total];
    });
    const totales = estadosConfig.map(estado =>
      departamentos.reduce((sum, depto) => sum + datosPorDepartamento[depto][estado.key], 0)
    );
    const totalGeneral = totales.reduce((a, b) => a + b, 0);
    rows.push(['TOTAL', ...totales, totalGeneral]);

    const csvContent = [
      headers,
      ...rows
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = `participantes_departamento_${dayjs().format('YYYY-MM-DD')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // 5. RENDERIZADO DEL COMPONENTE
  return (
    <Card
      hoverable
      style={{
        borderRadius: 12,
        ...(isExpanded && {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          margin: 20,
          backgroundColor: 'white'
        })
      }}
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 16,
        flexWrap: 'wrap',
        gap: 16
      }}>
        <div>
          <Title level={4} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            Participantes por Departamento
          </Title>
          <div style={{ fontSize: 14, color: '#666', marginTop: 4 }}>
            {`${totalGeneral} participantes totales - ${departamentos.length} departamentos`}
          </div>
        </div>

        <Space wrap>
          <Tooltip title={viewOption === 'count' ? "Mostrar distribución porcentual" : "Mostrar cantidad de participantes"}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '4px 12px',
              backgroundColor: '#f8f9fa',
              borderRadius: 6,
              border: '1px solid #dee2e6'
            }}>
              <span style={{
                fontSize: 13,
                color: viewOption === 'count' ? '#024589ff' : '#6c757d',
                fontWeight: viewOption === 'count' ? '600' : 'normal'
              }}>
                Cantidad
              </span>
              <Switch
                checked={viewOption === 'percentage'}
                onChange={(checked) => setViewOption(checked ? 'percentage' : 'count')}
                size="small"
              />
              <span style={{
                fontSize: 13,
                color: viewOption === 'percentage' ? '#024589ff' : '#6c757d',
                fontWeight: viewOption === 'percentage' ? '600' : 'normal'
              }}>
                Porcentaje
              </span>
            </div>
          </Tooltip>

          <div style={{ display: 'flex', gap: 8 }}>
            <Tooltip title="Cambiar tipo de gráfico">
              <Button
                icon={chartType === 'bar' ? <PieChartOutlined /> : <BarChartOutlined />}
                onClick={() => setChartType(chartType === 'bar' ? 'pie' : 'bar')}
                size="small"
                type={chartType === 'bar' ? 'default' : 'dashed'}
              >
                {chartType === 'bar' ? 'Pastel' : 'Barras'}
              </Button>
            </Tooltip>

            <Tooltip title="Exportar imagen del gráfico (alta calidad)">
              <Button
                icon={<PictureOutlined />}
                onClick={exportHighQualityImage}
                size="small"
                type="primary"
              >
                Imagen
              </Button>
            </Tooltip>

            <Tooltip title="Exportar datos en formato CSV">
              <Button
                icon={<FileExcelOutlined />}
                onClick={handleExportCSV}
                size="small"
              >
                CSV
              </Button>
            </Tooltip>

            <Tooltip title={isExpanded ? "Minimizar" : "Maximizar"}>
              <Button
                icon={<ExpandOutlined />}
                onClick={() => setIsExpanded(!isExpanded)}
                size="small"
                type={isExpanded ? "primary" : "default"}
              />
            </Tooltip>
          </div>
        </Space>
      </div>

      <div style={{
        height: isExpanded ? 'calc(100vh - 200px)' : 400,
        backgroundColor: '#FFFFFF'
      }}>
        {chartType === 'bar' ? (
          <Bar
            ref={chartRef}
            data={barChartData}
            options={getChartOptions(false)}
          />
        ) : (
          <Pie
            ref={chartRef}
            data={pieChartData}
            options={getChartOptions(true)}
          />
        )}
      </div>
    </Card>
  );
}